import { useEffect, useMemo, useState } from 'react';

import uniq from 'lodash-es/uniq';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import PlainFieldset from '@/components/atoms/Fieldset/templates/PlainFieldset';
import { ActivityLogs } from '@/components/pages/ActivityLogs/ActivityLogs';
import { FILTER_KEY, MAX_FILTER_VALUES } from '@/components/pages/ActivityLogs/constants';
import { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { useActivityLogs } from '@/hooks/useActivityLogs';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { subMonthsFromNow, subWeeksFromNow, formatDate } from '@/util/dateUtils';
import { urlSafeBase64Encode, isUuid } from '@/util/util';

import { FIELDSET_CARD_PADDING } from './constants';
import HomeAnalytics from './fieldsets/HomeAnalytics/HomeAnalytics';
import useHomeOverview from './fieldsets/HomeOverview/useHomeOverview';

import styles from './HomeCompleted.module.scss';

const HomeCompleted = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const deskId = useDeskId();
  const { desk } = useDesks(deskId);
  const { rulesWithBrains, isLoading } = useHomeOverview();

  // Capped because this also feeds the "View all" deep link, which the page hydrates uncapped.
  const brainParentIds = useMemo(
    () =>
      uniq(
        (rulesWithBrains?.map(({ brain }) => brain?.brain_parent_id) ?? []).filter(isUuid)
      ).slice(0, MAX_FILTER_VALUES),
    [rulesWithBrains]
  );

  const [isAnalyticsEmpty, setAnalyticsEmpty] = useState(false);
  useEffect(() => {
    setAnalyticsEmpty(false);
  }, [slug, deskId]);
  const canReadAnalytics = useSelector((state: RootState) =>
    getPermissions(state, 'analytics', actions.READ)
  );

  const canReadLogs = useSelector((state: RootState) =>
    getPermissions(state, 'audit_logs', actions.READ)
  );

  const getBrainProp = () => {
    if (brainParentIds.length > 1) {
      return { brain_ids: brainParentIds };
    }
    return { brainId: brainParentIds[0] };
  };

  const hasBrains = brainParentIds.length > 0;

  const brainProp = getBrainProp();
  const { isDataEmpty: isBrainEmpty } = useActivityLogs(slug, {
    deskId: null,
    brainIds: brainProp.brain_ids,
    brainId: brainProp.brainId,
    enabled: hasBrains,
  });
  const { isDataEmpty: isEnvironmentEmpty } = useActivityLogs(slug, {
    deskId,
    enabled: Boolean(deskId),
  });

  if (!deskId) {
    return null;
  }
  const deskFilter = desk
    ? [{ type: 'deskIds', deskIds: [{ value: desk.desk_id, label: desk.name }] }]
    : [];
  // Activity Logs reads chip filters in the `FilterItem` shape (useTableFilters), so its deep
  // links use that format — distinct from the legacy `deskFilter` the analytics link still uses.
  const deskLogFilter: FilterItem[] = desk
    ? [{ key: FILTER_KEY.desks, values: [desk.desk_id] }]
    : [];
  const brainLogFilter: FilterItem[] = brainParentIds.length
    ? [{ key: FILTER_KEY.brains, values: brainParentIds }]
    : [];

  const analyticsDate = {
    startDate: formatDate(subMonthsFromNow(1)),
    endDate: formatDate(new Date()),
  };

  const logsDate = { startDate: formatDate(subWeeksFromNow(1)), endDate: formatDate(new Date()) };
  const deskUrl = `/${slug}/account/activity-logs?date=${urlSafeBase64Encode(
    JSON.stringify(logsDate)
  )}&filters=${urlSafeBase64Encode(JSON.stringify(deskLogFilter))}`;
  const brainUrl = `/${slug}/account/activity-logs?date=${urlSafeBase64Encode(
    JSON.stringify(logsDate)
  )}&filters=${urlSafeBase64Encode(JSON.stringify(brainLogFilter))}`;

  const analyticsUrl = `/${slug}/analytics/overview?date=${urlSafeBase64Encode(
    JSON.stringify(analyticsDate)
  )}&filters=${urlSafeBase64Encode(JSON.stringify(deskFilter))}`;

  return (
    <div className={styles.container}>
      {canReadAnalytics && !isAnalyticsEmpty && (
        <div className={styles.analyticsWrapper}>
          <div className={styles.header}>
            <span className="text-sm leading-5 font-medium">{t('home.monthly_insights')}</span>
            <Link to={analyticsUrl} className={styles.link}>
              <span className="text-sm leading-5 font-normal text-foreground">
                {t('home.more_metrics')}
              </span>
            </Link>
          </div>
          <HomeAnalytics setAnalyticsEmpty={setAnalyticsEmpty} />
        </div>
      )}
      {canReadLogs && (
        <>
          {hasBrains && !isBrainEmpty && !isLoading && (
            <div className={styles.logWrapper}>
              <div className={styles.header}>
                <span className="text-sm leading-5 font-medium">{t('home.recent_ai_agent')}</span>
                <Link to={brainUrl} className={styles.link}>
                  <span className="text-sm leading-5 font-normal text-foreground">
                    {' '}
                    {t('home.view_all')}
                  </span>
                </Link>
              </div>
              <PlainFieldset fullWidth className={FIELDSET_CARD_PADDING}>
                <div className={styles.content}>
                  <ActivityLogs hideFilters {...getBrainProp()} deskId={null} />
                </div>
              </PlainFieldset>
            </div>
          )}
          {desk && !isLoading && !isEnvironmentEmpty && (
            <div className={styles.logWrapper}>
              <div className={styles.header}>
                <span className="text-sm leading-5 font-medium">
                  {t('home.recent_environment')}
                </span>
                <Link to={deskUrl} className={styles.link}>
                  <span className="text-sm leading-5 font-normal text-foreground">
                    {' '}
                    {t('home.view_all')}
                  </span>
                </Link>
              </div>

              <PlainFieldset fullWidth className={FIELDSET_CARD_PADDING}>
                <div className={styles.content}>
                  <ActivityLogs hideFilters deskId={deskId} brain_ids={[]} brainId={null} />
                </div>
              </PlainFieldset>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HomeCompleted;
